// ** React Imports
import { forwardRef, useEffect, useState, memo } from 'react';

// ** Third Party Components
import Proptypes from 'prop-types';
import { Badge } from 'reactstrap';
import classnames from 'classnames';
import ImageLoader from 'components/Image';
import { useOnlineChange } from 'hooks/messages';
import { useUser } from '../../../hooks';

const AvatarCustom = forwardRef((props, ref) => {
  // ** Props
  const {
    color,
    className,
    imgClassName,
    initials,
    size,
    badgeUp,
    content,
    icon,
    badgeColor,
    badgeText,
    img,
    imgHeight,
    imgWidth,
    status,
    tag: Tag,
    contentStyles,
    userId,
    dimension,
    ...rest
  } = props;

  const onlineChange = useOnlineChange();
  const auth = useUser();

  const [currentStatus, setCurrentStatus] = useState(
    userId !== auth?._id ? status : 'online',
  );

  useEffect(() => {
    if (userId && userId !== auth?._id && status !== currentStatus) {
      setCurrentStatus(status);
    }
  }, [status, userId]);

  useEffect(() => {
    if (
      userId &&
      userId !== auth?._id &&
      onlineChange &&
      onlineChange?.userId === userId &&
      onlineChange.online !== currentStatus
    ) {
      setCurrentStatus(onlineChange.online ? 'online' : 'offline');
    }
  }, [onlineChange]);

  // ** Function to extract initials from content
  const getInitials = (str) => {
    const results = [];
    const wordArray = str.split(' ');
    wordArray.forEach((e) => {
      results.push(e[0]);
    });
    return results.join('');
  };

  return (
    <Tag
      className={classnames('avatar', {
        [className]: className,
        [`bg-${color}`]: color,
        [`avatar-${size}`]: size,
      })}
      ref={ref}
      {...rest}
    >
      {img === false || img === undefined ? (
        <span
          className={classnames('avatar-content', {
            'position-relative': badgeUp,
          })}
          style={contentStyles}
        >
          {initials ? getInitials(content) : content}

          {icon || null}
          {badgeUp ? (
            <Badge
              color={badgeColor || 'primary'}
              className="badge-sm badge-up"
              pill
            >
              {badgeText || '0'}
            </Badge>
          ) : null}
        </span>
      ) : (
        <ImageLoader
          className={classnames({
            [imgClassName]: imgClassName,
          })}
          src={img}
          alt="avatarImg"
          height={imgHeight && !size ? imgHeight : 32}
          width={imgWidth && !size ? imgWidth : 32}
          objectFit="cover"
          size={dimension || 100}
        />
      )}
      {userId && auth?._id && currentStatus ? (
        <span
          className={classnames({
            [`avatar-status-${currentStatus}`]: currentStatus,
            [`avatar-status-${size}`]: size,
          })}
        />
      ) : null}
    </Tag>
  );
});

export default memo(AvatarCustom);

// ** PropTypes
AvatarCustom.propTypes = {
  userId: Proptypes.string,
  imgClassName: Proptypes.string,
  className: Proptypes.string,
  src: Proptypes.string,
  tag: Proptypes.oneOfType([Proptypes.func, Proptypes.string]),
  badgeUp: Proptypes.bool,
  content: Proptypes.string,
  icon: Proptypes.node,
  contentStyles: Proptypes.object,
  dimension: Proptypes.number,
  badgeText: Proptypes.string,
  imgHeight: Proptypes.oneOfType([Proptypes.string, Proptypes.number]),
  imgWidth: Proptypes.oneOfType([Proptypes.string, Proptypes.number]),
  size: Proptypes.oneOf(['sm', 'lg', 'xl']),
  status: Proptypes.oneOf(['online', 'offline', 'away', 'busy']),
  badgeColor: Proptypes.oneOf([
    'primary',
    'secondary',
    'success',
    'danger',
    'info',
    'warning',
    'dark',
    'light-primary',
    'light-secondary',
    'light-success',
    'light-danger',
    'light-info',
    'light-warning',
    'light-dark',
  ]),
  color: Proptypes.oneOf([
    'primary',
    'secondary',
    'success',
    'danger',
    'info',
    'warning',
    'dark',
    'light-primary',
    'light-secondary',
    'light-success',
    'light-danger',
    'light-info',
    'light-warning',
    'light-dark',
  ]),
  initials(props) {
    if (props.initials && props.content === undefined) {
      return new Error('content prop is required with initials prop.');
    }
    if (props.initials && typeof props.content !== 'string') {
      return new Error('content prop must be a string.');
    }
    if (typeof props.initials !== 'boolean' && props.initials !== undefined) {
      return new Error('initials must be a boolean!');
    }
  },
};

// ** Default Props
AvatarCustom.defaultProps = {
  tag: 'div',
};
